import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { brl, tablePrice, discountBadge } from "@/lib/api";

const Chip = ({ active, disabled, onClick, children, testId }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testId}
    className={`rounded-md border-2 px-4 py-2.5 font-mono text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${active ? "border-[#0099DD] bg-sky-50 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
    {children}
  </button>
);

const Group = ({ label, children }) => (
  <div className="space-y-2">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export function TablePicker({ product }) {
  const t = product.price_table;
  const has = (o, q) => !!tablePrice(product, o, q).price;
  const first = t.options.find((o) => t.quantities.some((q) => has(o, q)));
  const [option, setOption] = useState(first);
  const [qty, setQty] = useState(() => t.quantities.find((q) => has(first, q)));
  const { add } = useCart();
  const navigate = useNavigate();
  const { price, regular } = tablePrice(product, option, qty);
  const unit = product.unit_label || "unidade";

  const pickOption = (o) => {
    setOption(o);
    if (!has(o, qty)) setQty(t.quantities.find((q) => has(o, q)));
  };
  const handleAdd = () => {
    add(product, { quantity: qty, option, price, regular_price: regular });
    toast.success(`${product.name} adicionado ao carrinho`, { action: { label: "Ver carrinho", onClick: () => navigate("/carrinho") } });
  };

  return (
    <div className="space-y-5 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="price-table-picker">
      <p className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500">Monte seu pedido</p>
      <Group label={t.option_label || "Opção"}>
        {t.options.map((o, i) => <Chip key={o} active={o === option} disabled={!t.quantities.some((q) => has(o, q))} onClick={() => pickOption(o)} testId={`table-option-${i}`}>{o}</Chip>)}
      </Group>
      <Group label={`Quantidade (${unit})`}>
        {t.quantities.map((q, i) => <Chip key={q} active={q === qty} disabled={!has(option, q)} onClick={() => setQty(q)} testId={`table-qty-${i}`}>{q.toLocaleString("pt-BR")}</Chip>)}
      </Group>
      <div className="space-y-2 rounded-md bg-slate-900 p-5 text-white">
        <div className="flex justify-between gap-2 text-sm text-slate-300">
          <span data-testid="calc-selection">{option} · {qty?.toLocaleString("pt-BR")} {unit}(s)</span>
          {price > 0 && <span className="font-mono" data-testid="calc-unit-each">{brl(price / qty)} cada</span>}
        </div>
        <div className="flex items-end justify-between">
          <span className="text-sm text-slate-300">Valor do pacote{regular ? <span className={`ml-2 inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${discountBadge(product).cls}`} data-testid="calc-reseller-badge">{discountBadge(product).label}</span> : null}</span>
          <span className="text-right">
            {regular > 0 && <span className="mr-2 font-mono text-sm text-slate-400 line-through" data-testid="calc-regular-total">{brl(regular)}</span>}
            <span className="font-mono text-3xl font-bold text-[#38bdf8]" data-testid="calc-total">{brl(price || 0)}</span>
          </span>
        </div>
      </div>
      <Button onClick={handleAdd} disabled={!price} className="h-12 w-full rounded-full bg-[#E5007D] text-base font-bold hover:bg-[#c2006a]" data-testid="btn-adicionar-carrinho">
        <ShoppingBag className="h-5 w-5" /> Adicionar ao carrinho
      </Button>
    </div>
  );
}
